import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: { padding: '24px', fontFamily: 'inherit', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '22px', fontWeight: 700, color: '#1a1a1a', margin: 0 },
  controls: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  select: {
    padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '6px',
    fontSize: '14px', background: '#fff', cursor: 'pointer', minWidth: '160px',
  },
  btn: {
    padding: '8px 18px', fontSize: '13px', fontWeight: 600, border: 'none',
    borderRadius: '6px', cursor: 'pointer',
  },
  primaryBtn: {
    padding: '8px 18px', fontSize: '13px', fontWeight: 600, border: 'none',
    borderRadius: '6px', cursor: 'pointer', background: '#1e3a5f', color: '#fff',
  },
  card: {
    background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef',
    marginBottom: '24px', overflow: 'hidden',
  },
  cardHeader: {
    padding: '14px 20px', background: '#1e3a5f', color: '#fff',
    fontWeight: 600, fontSize: '15px',
  },
  cardBody: { padding: '20px' },
  statsRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' },
  statCard: {
    padding: '14px 18px', background: '#f8f9fa', borderRadius: '8px',
    border: '1px solid #e9ecef', minWidth: '130px', flex: '1 1 130px',
  },
  statLabel: { fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' },
  statValue: { fontSize: '22px', fontWeight: 700, color: '#1a1a1a' },
  statSub: { fontSize: '12px', color: '#6c757d', marginTop: '2px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '10px 12px', background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef', color: '#495057', fontWeight: 600,
    fontSize: '11px', textTransform: 'uppercase',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' },
  muted: { color: '#6c757d', fontSize: '13px' },
  pager: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px', padding: '10px 16px', background: '#f8f9fa',
    borderTop: '1px solid #e9ecef', flexWrap: 'wrap', fontSize: '13px',
  },
  pagerBtn: (disabled) => ({
    padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px',
    border: '1px solid #dee2e6', background: disabled ? '#f1f3f5' : '#fff',
    color: disabled ? '#adb5bd' : '#1e3a5f',
    cursor: disabled ? 'default' : 'pointer',
  }),
  pagerSelect: {
    padding: '5px 8px', border: '1px solid #dee2e6', borderRadius: '6px',
    fontSize: '13px', background: '#fff', cursor: 'pointer',
  },
  phaseBar: (pct, color) => ({
    height: '22px', borderRadius: '4px', background: color,
    width: `${Math.max(pct, 2)}%`, display: 'inline-block', marginRight: '2px',
  }),
  errorBanner: {
    padding: '12px 16px', background: '#fff3cd', borderRadius: '8px',
    color: '#856404', marginBottom: '16px', fontSize: '13px',
  },
  emptyState: {
    padding: '40px', background: '#f8f9fa', borderRadius: '8px',
    textAlign: 'center', color: '#6c757d', fontSize: '14px',
  },
};

const PHASE_COLORS = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
];

function formatDuration(seconds) {
  if (seconds == null) return '--';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatDate(s) {
  if (!s) return '--';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

// ── Component ────────────────────────────────────────────────────────────────

function System({ currentUser }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Fetch available organizations (exclude admin-type orgs with no database)
  useEffect(() => {
    axios.get('/api/config/organizations')
      .then(res => {
        const orgs = (res.data.data || []).filter(o => o.type !== 'admin');
        setOrganizations(orgs);
        if (orgs.length && !selectedOrg) setSelectedOrg(orgs[0].name);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPerformance = useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    setError(null);
    axios.get('/api/dachido/processing-performance', {
      params: { organization: selectedOrg, page, page_size: pageSize },
    })
      .then(res => {
        if (res.data.success) {
          setPerfData(res.data);
        } else {
          setError(res.data.error || 'Failed to load');
          setPerfData(null);
        }
      })
      .catch(err => {
        setError(err.response?.data?.error || err.message);
        setPerfData(null);
      })
      .finally(() => setLoading(false));
  }, [selectedOrg, page, pageSize]);

  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  // A different org (or page size) has its own run count — start from page 1.
  useEffect(() => { setPage(1); }, [selectedOrg, pageSize]);

  // The backend clamps an out-of-range page; follow it so Prev/Next stay sane.
  useEffect(() => {
    const served = perfData?.pagination?.page;
    if (served && served !== page) setPage(served);
  }, [perfData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render helpers ─────────────────────────────────────────────────────

  const renderSummary = () => {
    if (!perfData?.summary) return null;
    const sm = perfData.summary;
    return (
      <div style={s.statsRow}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Pipeline Runs</div>
          <div style={s.statValue}>{sm.total_runs ?? '--'}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Conversations</div>
          <div style={s.statValue}>{sm.total_conversations ?? '--'}</div>
          <div style={s.statSub}>summed across runs</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Total Audio</div>
          <div style={s.statValue}>{formatDuration(sm.total_audio_seconds)}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Total Processing</div>
          <div style={s.statValue}>{formatDuration(sm.total_processing_seconds)}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Audio / Processing</div>
          <div style={{ ...s.statValue, color: sm.ratio != null ? (sm.ratio >= 1 ? '#28a745' : '#dc3545') : '#6c757d' }}>
            {sm.ratio != null ? `${sm.ratio}x` : '--'}
          </div>
          <div style={s.statSub}>{sm.ratio != null ? (sm.ratio >= 1 ? 'faster than real-time' : 'slower than real-time') : 'no data'}</div>
        </div>
      </div>
    );
  };

  const renderPhaseBreakdown = () => {
    if (!perfData?.summary?.phase_totals) return null;
    const phaseTotals = perfData.summary.phase_totals;
    const entries = Object.entries(phaseTotals).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return null;

    const totalPhaseTime = entries.reduce((sum, [, v]) => sum + v, 0);

    return (
      <div style={s.card}>
        <div style={s.cardHeader}>Phase Breakdown (aggregated across the runs listed below)</div>
        <div style={s.cardBody}>
          {/* Stacked bar */}
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', marginBottom: '16px', height: '28px' }}>
            {entries.map(([name, secs], i) => {
              const pct = totalPhaseTime > 0 ? (secs / totalPhaseTime) * 100 : 0;
              return (
                <div
                  key={name}
                  title={`${name}: ${formatDuration(secs)} (${pct.toFixed(1)}%)`}
                  style={{ ...s.phaseBar(pct, PHASE_COLORS[i % PHASE_COLORS.length]), height: '100%' }}
                />
              );
            })}
          </div>
          {/* Legend table */}
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}></th>
                <th style={s.th}>Phase</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Duration</th>
                <th style={{ ...s.th, textAlign: 'right' }}>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([name, secs], i) => (
                <tr key={name}>
                  <td style={s.td}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: PHASE_COLORS[i % PHASE_COLORS.length] }} />
                  </td>
                  <td style={{ ...s.td, fontWeight: 500 }}>{name}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(secs)}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>
                    {totalPhaseTime > 0 ? `${((secs / totalPhaseTime) * 100).toFixed(1)}%` : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRunsTable = () => {
    if (!perfData?.runs?.length) return null;
    const pg = perfData.pagination || {};
    const curPage = pg.page ?? 1;
    const totalPages = pg.total_pages ?? 1;
    const totalRuns = pg.total_runs ?? perfData.runs.length;
    const firstRow = (curPage - 1) * (pg.page_size ?? pageSize) + 1;
    const lastRow = firstRow + perfData.runs.length - 1;
    return (
      <div style={s.card}>
        <div style={s.cardHeader}>Pipeline Runs</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Run ID</th>
                <th style={s.th}>Run At</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Processing</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Conversations</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Entities</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Mentions</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Audio</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Ratio</th>
              </tr>
            </thead>
            <tbody>
              {perfData.runs.map((run, i) => (
                <tr key={run.run_id || i}>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{run.run_id ?? '--'}</td>
                  <td style={s.td}>{formatDate(run.run_at)}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(run.run_seconds)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{run.conversations ?? '--'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{run.entities ?? '--'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{run.mentions ?? '--'}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(run.total_audio_seconds)}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: run.ratio != null ? (run.ratio >= 1 ? '#28a745' : '#dc3545') : '#6c757d' }}>
                    {run.ratio != null ? `${run.ratio}x` : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={s.pager}>
          <div style={s.muted}>
            Showing {firstRow}–{lastRow} of {totalRuns} runs
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={s.muted}>Rows per page:</span>
            <select
              style={s.pagerSelect}
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              style={s.pagerBtn(curPage <= 1 || loading)}
              disabled={curPage <= 1 || loading}
              onClick={() => setPage(curPage - 1)}
            >
              ← Prev
            </button>
            <span style={s.muted}>Page {curPage} of {totalPages}</span>
            <button
              style={s.pagerBtn(curPage >= totalPages || loading)}
              disabled={curPage >= totalPages || loading}
              onClick={() => setPage(curPage + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSchemaInfo = () => {
    if (!perfData?.schema) return null;
    const sc = perfData.schema;
    return (
      <div style={{ ...s.muted, marginTop: '8px' }}>
        Mode: <strong>{perfData.mode}</strong>
        {' | '}
        Tables: evt_pipeline_run {sc.has_evt_pipeline_run ? '✓' : '✗'}
        {', '}evt_phase_timing {sc.has_evt_phase_timing ? '✓' : '✗'}
        {', '}ref_pipeline_phase {sc.has_ref_pipeline_phase ? '✓' : '✗'}
        {' | '}
        Columns: run_id {sc.has_run_id ? '✓' : '✗'}
        {', '}call_duration_minutes {sc.has_call_duration ? '✓' : '✗'}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>System</h2>
        <div style={s.controls}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>Organization:</label>
          <select
            style={s.select}
            value={selectedOrg}
            onChange={e => setSelectedOrg(e.target.value)}
          >
            {organizations.map(o => (
              <option key={o.name} value={o.name}>{o.display_name}</option>
            ))}
          </select>
          <button style={s.primaryBtn} onClick={fetchPerformance} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div style={s.errorBanner}>Error: {error}</div>}

      {loading && !perfData && (
        <div style={s.emptyState}>Loading processing performance data...</div>
      )}

      {perfData && perfData.mode === 'none' && (
        <div style={s.emptyState}>
          <div style={{ marginBottom: '8px', fontWeight: 600 }}>No pipeline tables found</div>
          <div>The selected organization's database does not have <code>evt_pipeline_run</code> or <code>fact_conversation</code> tables.</div>
          {renderSchemaInfo()}
        </div>
      )}

      {perfData && perfData.mode !== 'none' && (
        <>
          {/* Summary cards */}
          {renderSummary()}

          {/* Phase breakdown */}
          {renderPhaseBreakdown()}

          {/* Runs table */}
          {renderRunsTable()}

          {perfData.runs?.length === 0 && (
            <div style={s.emptyState}>No completed pipeline runs found for this organization.</div>
          )}

          {/* Schema info footer */}
          {renderSchemaInfo()}
        </>
      )}
    </div>
  );
}

export default System;
